import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setWasmUrl } from '@lottiefiles/dotlottie-react';
import App from './App.tsx';
// Fonts: Inter + Arabic/Persian families ship via @fontsource (build-time static assets from your origin).
// Browsers cache .woff2 like other chunks — no per-visit fetch from Google Fonts unless cache was cleared.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
/* Bundled Persian fonts (OFL) — no runtime fetch from Google Fonts (v5: import package root, not /index.css) */
import '@fontsource/vazirmatn';
import '@fontsource/amiri';
import '@fontsource/lateef';
import '@fontsource/noto-naskh-arabic';
import './index.css';
import './i18n';
import { bootstrapLocaleDocument } from './hooks/useLocale';
import { initializeFontSystem } from './utils/fontLoader';
import { logEnvConfig } from './utils/envConfig';
import './utils/globalDiagnostics';

const PWA_SHELL_VERSION_KEY = 'pwa-shell-version-seen';

// Use local WASM to avoid CDN/cache/network errors (ERR_CACHE_READ_FAILURE, streaming compile failures)
setWasmUrl('/dotlottie-player.wasm');

// Recover from failed dynamic imports (e.g. stale cache, chunk 404) by reloading once
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', () => {
    window.location.reload();
  });
}

// Initialize Persian font system
bootstrapLocaleDocument();
initializeFontSystem();

// Log environment configuration in development
logEnvConfig();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

async function resolvePwaShellVersion(): Promise<string> {
  try {
    const response = await fetch('/api/System/pwa-release-info', { cache: 'no-store' });
    if (!response.ok) return '1';
    const info = (await response.json()) as { shellVersion?: number };
    return String(info.shellVersion ?? 1);
  } catch {
    return '1';
  }
}

function ensureManifestLink(): void {
  const existing = document.querySelector('link[rel="manifest"]');
  if (existing) {
    existing.setAttribute('href', '/api/System/manifest.webmanifest');
    return;
  }
  const link = document.createElement('link');
  link.id = 'app-webmanifest';
  link.rel = 'manifest';
  link.href = '/api/System/manifest.webmanifest';
  document.head.appendChild(link);
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    ensureManifestLink();

    const hadControllerAtLoad = !!navigator.serviceWorker.controller;
    let reloadedForNewWorker = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloadedForNewWorker || !hadControllerAtLoad) return;
      reloadedForNewWorker = true;
      window.location.reload();
    });

    void (async () => {
      const shellVersion = await resolvePwaShellVersion();
      if (!localStorage.getItem(PWA_SHELL_VERSION_KEY)) {
        localStorage.setItem(PWA_SHELL_VERSION_KEY, shellVersion);
      }

      try {
        await navigator.serviceWorker.register(`/service-worker.js?v=${shellVersion}`);
      } catch (error) {
        console.warn('Service worker registration failed:', error);
      }
    })();
  });
}
